
import { OPEN_DRAWER_PAGE, SET_FIELD_INVALID, SET_FLYOUT_VERIFIED } from 'actions';
import { ADDITIONAL_USER_INPUTS_PAGE, LOCATION_AND_LOADS_PAGE, MODULE_SELECTION_PAGE, RAIL_SYSTEM_PAGE } from 'projectDesign/components/projectConfiguration/utils/projectConfigurationDrawerPages';
import * as React from 'react';
import SVG from 'react-inlinesvg';
import { Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { dispatch, state } from '__common/store';
import CustomTooltip from '__common/components/CustomTooltip';
import { isASCE722BuildingCode } from 'projectDesign/components/projectConfiguration/projectConfiguration';
import { disabled722BuildingCodeProducts, isULA } from '__common/constants/products';
import { apiField as buildingCodeApiField } from 'projectDesign/components/projectConfiguration/fields/buildingCode';
import { ACCESS_FOR_7_22_PERMISSION } from 'user/userGroups';
import { ulaClampOptions } from 'projectDesign/components/projectConfiguration/utils/constants';
import { applyAsce722AccessForEcoFoot2Plus, applyAsce722AccessForRM10Evo } from '__common/utils/versionCompare/versionCompare';

import { apiField as clampTypeChoiceApiField } from "projectDesign/components/projectConfiguration/fields/clampTypeChoice";

type Props = {
  label?: string,
  onClick: () => void,
  page?: string
  verifiedFlyouts?: verifiedFlyouts,
  designSidebar?: boolean,
  disabled?: boolean,
  disabledReason?: string,
};

export default class SaveChangesButton extends React.Component<Props> {

  render() {
    const { label, page, verifiedFlyouts, onClick, designSidebar, disabled, disabledReason } = this.props;
    const { projectConfiguration: { projectEnvConfig, productId, projectVersion }, user: { permissions } } = state();
    const isAuroraRackingTypeSM = projectEnvConfig?.aurora_racking_type && projectEnvConfig?.aurora_racking_type === 9 ? true : false;
    const isChecked = verifiedFlyouts?.[page]?.checked;
    const hasError = verifiedFlyouts?.[page]?.error;
    const verificationDisabled = Boolean(hasError || (designSidebar && disabled));
    const disabledHelp = disabledReason || 'Correct the highlighted required inputs before verifying this section.';
    const verificationLabel = label || (
      designSidebar
        ? verificationDisabled ? 'Correct highlighted inputs first' : isChecked ? 'Inputs verified' : 'Verify inputs'
        : 'VERIFY YOUR INPUTS'
    );

    const verifiableFlyouts = [
      MODULE_SELECTION_PAGE,
      LOCATION_AND_LOADS_PAGE,
      RAIL_SYSTEM_PAGE,
      ADDITIONAL_USER_INPUTS_PAGE
    ];

    function onChangeHandler(): void {
      if (isASCE722BuildingCode(projectEnvConfig.building_code) && !applyAsce722AccessForRM10Evo(productId, projectVersion) && !applyAsce722AccessForEcoFoot2Plus(productId, projectVersion) && !permissions.includes(ACCESS_FOR_7_22_PERMISSION) && disabled722BuildingCodeProducts(productId) && page === 'location-and-loads' ){
        dispatch(SET_FLYOUT_VERIFIED(LOCATION_AND_LOADS_PAGE, false, true));
        dispatch(SET_FIELD_INVALID(buildingCodeApiField, `Incompatible with ASCE 7-22. Please choose an appropriate building code.`));
        return;
      }
      if(isULA(productId) && projectEnvConfig.clamp_type === ulaClampOptions.PRO_SERIES && page === 'rail-system'){
        dispatch(SET_FLYOUT_VERIFIED(RAIL_SYSTEM_PAGE, false, true));
        dispatch(SET_FIELD_INVALID(clampTypeChoiceApiField, `Incompatible with PRO SERIES clamp option. Please choose an appropriate clamp option.`));
        return;
      }

     const isCurrentlyChecked = verifiedFlyouts[page]?.checked;
const newCheckedValue = !isCurrentlyChecked;

if (!newCheckedValue) {
  dispatch(SET_FLYOUT_VERIFIED(page, false));
  return;
}

const nextUnverifiedFlyout = verifiableFlyouts.find(
  flyoutPage =>
    flyoutPage !== page &&
    verifiedFlyouts[flyoutPage] &&
    !verifiedFlyouts[flyoutPage].checked
);



dispatch(SET_FLYOUT_VERIFIED(page, true));

if (nextUnverifiedFlyout) {
  dispatch(OPEN_DRAWER_PAGE(nextUnverifiedFlyout));
} else {
  
  onClick();
}
    }

    function shouldVerifyTheFlyout(pageName) {
      return verifiableFlyouts.includes(pageName)
    }

    return (
      <div
        className={`save-changes-container-simple ${isAuroraRackingTypeSM ? 'aurora-sm' : ''} ${designSidebar ? 'save-changes-container-simple--design-sidebar' : ''}`}
        onAnimationEnd={designSidebar ? (event) => {
          if (
            event.animationName === 'dsp-verification-attention'
            || event.animationName === 'dsp-verification-attention-reduced'
          ) {
            event.currentTarget.classList.remove('missing-element-highlight');
          }
        } : undefined}
      >
        {
          shouldVerifyTheFlyout(page) ? (
            designSidebar ? (
              <div className='verification-simple'>
                <div className='verification-simple__content'>
                  <Checkbox
                    className='verification-checkbox-simple'
                    id={page}
                    name={page}
                    checked={isChecked}
                    disabled={verificationDisabled}
                    onChange={onChangeHandler}
                    // sx={{
                    //   color: '#4055dd',
                    //   '&.Mui-checked': { color: '#4055dd' },
                    // }}
                  />
                  <label
                    htmlFor={page}
                    className={`verification-label-simple ${isChecked ? 'verified' : ''} ${verificationDisabled ? 'disabled' : ''}`}
                    title={verificationDisabled ? disabledHelp : undefined}
                  >
                    {verificationLabel}
                  </label>
                  <CustomTooltip
                    title={verificationDisabled
                      ? disabledHelp
                      : 'Correct input results in correct design. Please verify that all the inputs are accurate before proceeding.'}
                    placement='top'
                    arrow
                  >
                    <InfoOutlinedIcon className="flyout-info-icon" />
                  </CustomTooltip>
                </div>
              </div>
            ) : (
              <div className='save-changes-container'>
                <div className='inputs-verified-container'>
                  <Checkbox
                    className='save-changes-button inputs-verified-checkbox'
                    id={page}
                    name={page}
                    checked={isChecked}
                    disabled={verificationDisabled}
                    onChange={onChangeHandler}
                  />
                  <div
                    id='save-changes'
                    className='save-changes-button'
                    onClick={!verificationDisabled ? onChangeHandler : undefined}
                  >
                    {label || 'INPUTS VERIFIED '}
                    <CustomTooltip
                      title={verificationDisabled
                        ? disabledHelp
                        : 'Correct input results in correct design. Please verify that all the inputs are accurate before proceeding.'}
                      placement='right'
                      arrow
                    >
                      <InfoOutlinedIcon className="verify-inputs-info flyout-info-icon" />
                    </CustomTooltip>
                  </div>
                </div>
              </div>
            )
          ) : (!projectEnvConfig?.is_aurora_project && !projectEnvConfig?.is_os_project) ? (
            <div className='verification-simple verification-simple--save-changes'>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckIcon style={{ fontSize: '24px', color: '#003B5C' }} />
                <button
                  onClick={onClick}
                  className='save-button-simple'
                  style={{ border: 'none', background: 'transparent', color: '#003B5C', fontFamily: 'Oswald', fontWeight: '500', cursor: 'pointer', fontSize: '0.9rem', padding: '0' }}
                >
                  {label || 'SAVE CHANGES'}
                </button>

              </div>
            </div>
          ) : null
        }
      </div>
    );
  }
}
